import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { auth } from '../services/firebase'; 
import { onAuthStateChanged } from 'firebase/auth';
import ReactMarkdown from 'react-markdown';
import ChartBlock from './ChartBlock';
import LoadingSpinner from './LoadingSpinner';
import { ReactComponent as CircleIcon } from '../assets/icons/CircleIcon.svg';
import { ReactComponent as EditIcon } from '../assets/icons/EditIcon.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/DeleteIcon.svg';
import { ReactComponent as ChatArrow} from '../assets/icons/ChatArrow.svg';
import ConfirmationModal from './ContactSideBar/ConfirmationModal';
const generateUniqueId = () => `thread-${Date.now()}`;
const FLASK_URL = 'https://druida-288175208699.europe-west1.run.app';
// Etiquetas amigables para el evento `tool` del stream (qué está consultando Druida).
const TOOL_LABELS = {
    metrica: 'los números del negocio',
    movimientos: 'los movimientos de stock',
    estado_actual: 'el estado actual',
    ficha: 'la ficha completa',
    buscar: 'los registros',
    glosario: 'el glosario de datos',
};
// Los bloques ```chart de Druida se renderizan como gráficos (ChartBlock);
// el resto de los code blocks se muestran normal.
const markdownComponents = {
    code({ className, children, ...props }) {
        const contenido = Array.isArray(children) ? children.join('') : String(children ?? '');
        if (/language-chart/.test(className || '')) {
            return <ChartBlock payload={contenido} />;
        }
        return <code className={className} {...props}>{children}</code>;
    },
};
// Todos los endpoints del backend exigen el ID token de Firebase.
const getAuthHeaders = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('No hay usuario autenticado.');
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
};
const Chat = () => {
const [messages, setMessages] = useState([]);
const [input, setInput] = useState('');
const [isLoading, setIsLoading] = useState(false);
const [isInitialLoading, setIsInitialLoading] = useState(true); 
const [userId, setUserId] = useState(null);
const [currentThreadId, setCurrentThreadId] = useState(generateUniqueId());
const [showDeleteModal, setShowDeleteModal] = useState(false); 
const [threadToDeleteId, setThreadToDeleteId] = useState(null);
const [threads, setThreads] = useState([]);
const [lastThreadCursor, setLastThreadCursor] = useState(null);
const [hasMoreThreads, setHasMoreThreads] = useState(true);
const [isFetchingThreads, setIsFetchingThreads] = useState(false);
const threadListRef = useRef(null);
const messagesEndRef = useRef(null);
const [loadingText, setLoadingText] = useState('Consultando a los astros...');
const [isStreamingResponse, setIsStreamingResponse] = useState(false);
const scrollToBottom = () => {
messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
};
 useEffect(() => {
        scrollToBottom();
    }, [messages]);
const fetchHistory = async (uid, threadId) => {
if (!uid || !threadId) return;
try {
const headers = await getAuthHeaders();
const response = await axios.get(`${FLASK_URL}/history/${threadId}`, { headers });
const storedMessages = response.data.messages;
if (storedMessages && storedMessages.length > 0) {
const formattedMessages = storedMessages
.filter(msg => msg.text)
.map((msg, index) => ({
text: msg.text,
sender: msg.role === 'user' ? 'user' : 'agent',
id: msg.timestamp + index,
}));
setMessages(formattedMessages);
console.log(`✅ Historial de Hilo ${threadId} cargado: ${formattedMessages.length} mensajes.`);
} else {
setMessages([]);
}
} catch (error) {
console.error(`Error al cargar el historial del hilo ${threadId}:`, error);
setMessages([]);
}
};
useEffect(() => {
const unsubscribe = onAuthStateChanged(auth, (user) => {
if (user) {
setUserId(user.uid);
// Llama a fetchThreads para la carga inicial: cursor=null, initialLoad=true
fetchThreads(user.uid, null, true); 
} else {
setUserId(null);
setMessages([]);
setThreads([]);
setCurrentThreadId(generateUniqueId());
setIsInitialLoading(false); 
}
});
return () => unsubscribe();
}, []);
const handleThreadSelect = (threadId) => {
    if (!userId || threadId === currentThreadId) return;
    setCurrentThreadId(threadId);
    setMessages([]);
    fetchHistory(userId, threadId);
};
const handleNewChat = () => {
setCurrentThreadId(generateUniqueId());
setMessages([]);
setInput('');
console.log("Iniciando nuevo hilo de conversación.");
};
const handleScroll = () => {
    const element = threadListRef.current;
    if (element && hasMoreThreads && !isFetchingThreads) {
        const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50; 
        if (isNearBottom) {
            console.log("Cargando más hilos...");
            fetchThreads(userId, lastThreadCursor, false); 
        }
    }
};
useEffect(() => {
    const listElement = threadListRef.current;
    if (listElement) {
        listElement.addEventListener('scroll', handleScroll);
        return () => listElement.removeEventListener('scroll', handleScroll);
    }
}, [hasMoreThreads, isFetchingThreads, lastThreadCursor, userId]);
const fetchThreads = async (uid, cursor = null, initialLoad = true) => {
    if (isFetchingThreads || (!hasMoreThreads && !initialLoad)) return;
    setIsFetchingThreads(true);
    let url = `${FLASK_URL}/threads/${uid}`;
    if (cursor) {
        url += `?last_timestamp=${cursor}`;
    }
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get(url, { headers });
        const { threads: newThreads, last_cursor, limit_reached } = response.data;
        if (initialLoad) {
            setThreads(newThreads || []);
        } else {
            setThreads(prevThreads => {
                const existingIds = new Set(prevThreads.map(t => t.thread_id));
                const filteredNewThreads = newThreads.filter(t => !existingIds.has(t.thread_id));
                return [...prevThreads, ...filteredNewThreads];
            });
        }
        setLastThreadCursor(last_cursor);
        setHasMoreThreads(!limit_reached); 
    } catch (error) {
        console.error("Error al listar los hilos de conversación:", error);
        if (initialLoad) {
            setThreads([]);
            setCurrentThreadId(generateUniqueId());
        }
    } finally {
        setIsFetchingThreads(false);
        if (initialLoad) {
            setIsInitialLoading(false);
        }
    }
}
const handleSubmit = async (e) => {
e.preventDefault();
if (!input.trim() || isLoading || !userId || !currentThreadId) {
if (!userId) {
console.error("Intento de enviar mensaje sin ID de usuario.");
}
return;
}
const userMessage = { text: input, sender: 'user', id: Date.now() };
setMessages(prevMessages => [...prevMessages, userMessage]);
const userPrompt = input;
setInput('');
setIsLoading(true);
setIsStreamingResponse(false);
setLoadingText('Consultando a los astros...');
const isNewThread = messages.length === 0;
// La respuesta del agente se va completando a medida que llegan los deltas.
const agentMessageId = Date.now() + 1;
let agentText = '';
const upsertAgentMessage = (text) => {
setMessages(prevMessages => {
if (prevMessages.some(m => m.id === agentMessageId)) {
return prevMessages.map(m => (m.id === agentMessageId ? { ...m, text } : m));
}
return [...prevMessages, { text, sender: 'agent', id: agentMessageId }];
});
};
try {
const headers = await getAuthHeaders();
// SSE con fetch (axios no soporta streaming en el browser).
const response = await fetch(`${FLASK_URL}/chat/stream`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', ...headers },
body: JSON.stringify({ prompt: userPrompt, thread_id: currentThreadId }),
});
if (!response.ok || !response.body) {
throw new Error(`HTTP ${response.status}`);
}
const reader = response.body.getReader();
const decoder = new TextDecoder();
let buffer = '';
let streamError = null;
while (true) {
const { done, value } = await reader.read();
if (done) break;
buffer += decoder.decode(value, { stream: true });
const rawEvents = buffer.split('\n\n');
buffer = rawEvents.pop(); // lo que quede incompleto espera al próximo chunk
for (const rawEvent of rawEvents) {
const dataLine = rawEvent.split('\n').find(line => line.startsWith('data: '));
if (!dataLine) continue;
const event = JSON.parse(dataLine.slice(6));
if (event.type === 'delta') {
agentText += event.text;
setIsStreamingResponse(true);
upsertAgentMessage(agentText);
} else if (event.type === 'reset') {
// El backend descarta el texto que el modelo escribe ANTES de consultar
// (son cifras inventadas: todavía no vio los datos). Limpiamos lo ya
// mostrado para que no quede una respuesta especulativa en pantalla.
agentText = '';
upsertAgentMessage(agentText);
} else if (event.type === 'tool') {
setLoadingText(`Consultando ${TOOL_LABELS[event.name] || 'los registros'}...`);
} else if (event.type === 'done') {
agentText = event.response || agentText;
upsertAgentMessage(agentText);
} else if (event.type === 'error') {
streamError = event.message;
}
}
}
if (streamError && !agentText) {
throw new Error(streamError);
}
if (isNewThread) {
setThreads(prevThreads => [{
thread_id: currentThreadId,
title: userPrompt.substring(0, 50).trim() + (userPrompt.length > 50 ? "..." : ""),
last_updated: new Date().toISOString()
}, ...prevThreads]);
}
} catch (error) {
console.error("Error al chatear:", error);
upsertAgentMessage("Lo siento, ocurrió un error al comunicarme.");
} finally {
setIsLoading(false);
setIsStreamingResponse(false);
}
};
const handleThreadDelete = (e, threadId) => {
    e.stopPropagation();
    setThreadToDeleteId(threadId);
    setShowDeleteModal(true);
};
const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setThreadToDeleteId(null);
};
const handleConfirmDelete = async () => {
    const threadId = threadToDeleteId;
    if (!threadId) return;
    setShowDeleteModal(false);
    setThreadToDeleteId(null);
    try {
        const headers = await getAuthHeaders();
        await axios.delete(`${FLASK_URL}/history/${threadId}`, { headers });
        console.log(`Hilo ${threadId} eliminado.`);
        await fetchThreads(userId);
        if (threadId === currentThreadId) {
            handleNewChat();
        }
    } catch (error) {
        console.error(`Error al eliminar el hilo ${threadId}:`, error);
        alert("Ocurrió un error al intentar eliminar la conversación.");
    }
};
const isButtonDisabled = isLoading || !input.trim() || !userId;
const textareaRef = useRef(null);
useEffect(() => {
if (textareaRef.current) {
textareaRef.current.style.height = 'auto';
textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
}
}, [input]);
    if (isInitialLoading) {
        return (
<LoadingSpinner/>
        ); 
    }
return (
<div  style={{height:'100%', overflow:'hidden',display:'flex',flexDirection:'column'} }>
<header className="trazabilidad-header">
<div className="header-content" style={{padding:'0 1em 10px'}}>
<h1>Druida</h1>
</div>
</header>
<div className="main-chat-layout"> 
{}
<div className="chat-sidebar">
<div onClick={handleNewChat} className="new-chat-button" style={{fontSize:'16px'}}>
<EditIcon  style={{width:'13px',height:'13px',overflow:'visible',fill:'#4d4d4d'}} />Nueva conversación
</div>
<div className="thread-list-container">
<div className="thread-list" ref={threadListRef}> 
<label>Historial</label>
{threads.map((thread) => (
<div
key={thread.thread_id}
className={`thread-item ${thread.thread_id === currentThreadId ? 'active' : ''}`}
onClick={() => handleThreadSelect(thread.thread_id)}
>
<span className="thread-title" style={{ cursor: 'pointer', maxWidth: '185px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle',fontSize: '16px',margin:'0' }}>{thread.title}</span>
<div
className="delete-thread-button"
onClick={(e) => handleThreadDelete(e, thread.thread_id)}
style={{ 
border: 'none', 
cursor: 'pointer',
alignItems:'center',
justifyContent:'center',
margin:'auto',
marginRight:'0',
}}
>
<DeleteIcon 
style={{ 
height: '60%', 
width:'auto',
overflow: 'visible', 
margin:'4px auto 0px',
fill: '#4d4d4d' 
}} 
/>
</div>
</div>
))}
{isFetchingThreads && !isInitialLoading && (
    <div style={{ padding: '10px', textAlign: 'center' }}>
        Cargando más...
    </div>
)}
{!hasMoreThreads && threads.length > 0 && ( /* <--- NUEVO INDICADOR */
    <div style={{ padding: '10px 0', textAlign: 'center', fontSize: '0.8em', color: '#888' }}>
    </div>
)}
</div>
</div>
</div>
<div className="chat-container">
<div className="messages-list-wrapper">
<div className="messages-list">
{messages.length === 0 && (
<h2 className="initial-title">
{threads.some(t => t.thread_id === currentThreadId) 
? `¿Continuamos?` 
: '¿Cómo te puedo asistir?'
}
</h2>
)}
{messages.map((msg, index) => (
<div 
key={index} 
className={`message-bubble-container ${msg.sender} animated-message`}
>
{}
{msg.sender === 'agent' && (
<CircleIcon className="agent-icon" alt="Icono de Druida" />
)}
<div className={`message-bubble ${msg.sender}`}>
{msg.sender === 'agent' ? (
<ReactMarkdown components={markdownComponents}>{msg.text}</ReactMarkdown>
) : (
msg.text
)}
</div>
</div>
))}
{}
{isLoading && !isStreamingResponse && (
<div className="message-bubble-container agent">
<CircleIcon className="agent-icon loading-animation color-shift" alt="Icono de carga" />
<div className="message-bubble agent loading">
<span className="loading-text fade-text">
{loadingText}
</span>
</div>
</div>
)}
<div ref={messagesEndRef} />
</div>
</div>
<div className="scroll-fade-effect"></div> 
<form onSubmit={handleSubmit} className="input-form">
<div className="input-container-wrapper"> 
<textarea
ref={textareaRef}
value={input}
onChange={(e) => setInput(e.target.value)}
onKeyDown={(e) => {
if (e.key === 'Enter') {
if (e.ctrlKey || e.shiftKey) {
return;
}
e.preventDefault();
handleSubmit(e);
}
}}
placeholder="Consulta a Druida"
/>
<button type="submit" disabled={isButtonDisabled} className="send-button">
<ChatArrow className="send-arrow" alt='send-arrow' />
</button>
</div>
</form>
</div>
</div>
<ConfirmationModal
isVisible={showDeleteModal}
message="¿Estás seguro de que quieres eliminar esta conversación? Esta acción es irreversible."
onConfirm={handleConfirmDelete}
onCancel={handleCancelDelete}
/>
</div>
);
};
export default Chat;